"use client";

import type { ReactNode } from "react";
import { Alert } from "../../elements/alert/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../elements/dialog/dialog";
import { Text } from "../../elements/text/text";
import { cn } from "../../lib/utils";
import { ModalFooter } from "./modal-footer";

export type ButtonVariant =
  | "default"
  | "secondary"
  | "destructive"
  | "outline"
  | "outline-no-hover"
  | "ghost"
  | "link";

type GenericModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  primaryButtonText?: string;
  isPrimaryButtonDisabled?: boolean;
  formId?: string;
  secondaryButtonText?: string;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  primaryButtonVariant?: ButtonVariant;
  secondaryButtonVariant?: ButtonVariant;
  preventCloseOnOutsideClick?: boolean;
  modalClassName?: string;
  showDeleteButton?: boolean;
  onDelete?: () => void;
  deleteButtonText?: string;
  isLoading?: boolean;
  generalErrorMessage?: string;
  tertiaryButtonText?: string;
  onTertiaryAction?: () => void;
  isTertiaryButtonDisabled?: boolean;
  tertiaryButtonVariant?: ButtonVariant;
};

export function GenericModal(props: Readonly<GenericModalProps>) {
  const handleOpenChange = (open: boolean) => {
    if (!(open || props.preventCloseOnOutsideClick)) {
      props.onClose();
    }
  };
  // Extract form-related attributes so JSX stays readable
  const formAttributes = props.formId ? { form: props.formId } : {};
  return (
    <Dialog onOpenChange={handleOpenChange} open={props.isOpen}>
      <DialogContent
        className={cn(props.modalClassName ?? "max-w-[894px]! p-0")}
        onInteractOutside={(e) => {
          if (props.preventCloseOnOutsideClick) {
            e.preventDefault();
          }
        }}
      >
        <div
          className={cn(
            "flex max-h-[90vh] flex-col gap-6 overflow-y-auto p-16"
          )}
        >
          {/* Title */}
          <DialogHeader>
            <DialogTitle className="font-bold text-4xl">
              {props.title}
            </DialogTitle>
            {props.subtitle ? (
              <Text variant="body-md">{props.subtitle}</Text>
            ) : null}
          </DialogHeader>

          {/* General Error Message */}
          {props.generalErrorMessage ? (
            <Alert
              microcopies={{ title: props.generalErrorMessage }}
              variant="destructive"
            />
          ) : null}

          {/* Content */}
          <div>{props.children}</div>

          <ModalFooter
            deleteButtonText={props.deleteButtonText}
            formAttributes={formAttributes}
            formId={props.formId}
            isLoading={props.isLoading}
            isPrimaryButtonDisabled={props.isPrimaryButtonDisabled}
            isTertiaryButtonDisabled={props.isTertiaryButtonDisabled}
            onClose={props.onClose}
            onDelete={props.onDelete}
            onPrimaryAction={props.onPrimaryAction}
            onSecondaryAction={props.onSecondaryAction}
            onTertiaryAction={props.onTertiaryAction}
            primaryButtonText={props.primaryButtonText}
            secondaryButtonText={props.secondaryButtonText}
            secondaryButtonVariant={props.secondaryButtonVariant}
            showDeleteButton={props.showDeleteButton}
            tertiaryButtonText={props.tertiaryButtonText}
            tertiaryButtonVariant={props.tertiaryButtonVariant}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
